// React Query hooks for the WB drivers admin surface (Phase C3 Group 3).
//
// Pairs with /api/v1/wb/drivers/* — list (paginated + searchable) /
// detail / create / patch / delete plus the multipart signature upload.
// DTOs mirror server/src/modules/wb/drivers/types.ts exactly.
//
// CRITICAL: the C3 prompt initially referenced a PIN endpoint; that does
// NOT exist on the server. The real authentication artefact is a
// signature image stored via /api/v1/files/* — POST /:enc_id/signature
// uploads a multipart form (field name `signature`, 2 MB cap, PNG/JPEG/
// WEBP only) and updates the driver row's `signature_image_url` to the
// storage key. The display path uses /files/presigned to resolve the
// key to a readable URL.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Client-side types (mirror WbDriverDto on the server) ------------------

export interface WbDriverHaulierSummary {
  readonly enc_id: string;
  readonly name: string;
}

export interface WbDriver {
  readonly enc_id: string;
  readonly name: string;
  readonly licence_number: string | null;
  readonly phone: string | null;
  readonly default_haulier: WbDriverHaulierSummary | null;
  readonly signature_image_url: string | null;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface Pagination {
  readonly page: number;
  readonly per_page: number;
  readonly total: number;
  readonly total_pages: number;
}

export interface CreateDriverInput {
  name: string;
  licence_number?: string | null;
  phone?: string | null;
  default_haulier_id?: string | null;
  is_active?: boolean;
}

export type UpdateDriverInput = Partial<CreateDriverInput>;

export interface ListDriversParams {
  page?: number;
  per_page?: number;
  name?: string;
  /** Filter by `default_haulier_id`. Decrypted server-side. */
  haulier_enc_id?: string;
  is_active?: boolean;
}

interface ListDriversResponse {
  drivers: WbDriver[];
  pagination: Pagination;
}

// -- Helpers ----------------------------------------------------------------

function buildQueryString(params: ListDriversParams): string {
  const sp = new URLSearchParams();
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.per_page !== undefined) sp.set('per_page', String(params.per_page));
  if (params.name !== undefined && params.name !== '') sp.set('name', params.name);
  if (params.haulier_enc_id !== undefined && params.haulier_enc_id !== '') {
    sp.set('haulier_enc_id', params.haulier_enc_id);
  }
  if (params.is_active !== undefined) sp.set('is_active', String(params.is_active));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

// -- Queries ----------------------------------------------------------------

export function useWbDrivers(params: ListDriversParams = {}) {
  return useQuery<ListDriversResponse>({
    queryKey: queryKeys.wb.drivers(params as Record<string, unknown>),
    queryFn: async () => {
      const qs = buildQueryString(params);
      const res = await apiClient.get<ListDriversResponse>(
        `${endpoints.wb.drivers}${qs}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useWbDriver(encId: string | undefined) {
  const enabled = !!encId && encId !== 'new';
  return useQuery<{ driver: WbDriver }>({
    queryKey: queryKeys.wb.driver(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbDriver called without enc_id');
      const res = await apiClient.get<{ driver: WbDriver }>(
        endpoints.wb.driver(encId),
      );
      return res.data;
    },
    enabled,
    staleTime: 30_000,
  });
}

// -- Mutations --------------------------------------------------------------

export function useCreateDriver() {
  const qc = useQueryClient();
  return useMutation<{ driver: WbDriver }, Error, CreateDriverInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ driver: WbDriver }, CreateDriverInput>(
        endpoints.wb.drivers,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.drivers() });
    },
  });
}

export function useUpdateDriver(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ driver: WbDriver }, Error, UpdateDriverInput>({
    mutationFn: async (body) => {
      const res = await apiClient.patch<{ driver: WbDriver }, UpdateDriverInput>(
        endpoints.wb.driver(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.drivers() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.driver(encId) });
    },
  });
}

export function useDeleteDriver(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: true }>(
        endpoints.wb.driver(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.drivers() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.driver(encId) });
    },
  });
}

/**
 * Multipart signature upload. We bypass the JSON-coercing apiClient.post
 * wrapper and go through the raw axios instance — letting axios set the
 * correct multipart Content-Type with the generated boundary header.
 * Server enforces a 2 MB cap (returns 413 on breach) and validates the
 * MIME against `image/png | image/jpeg | image/webp`.
 */
export function useUploadDriverSignature(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ driver: WbDriver }, Error, File>({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('signature', file);
      const res = await apiClient.raw.post<{
        success: true;
        data: { driver: WbDriver };
      }>(endpoints.wb.driverSignature(encId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.drivers() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.driver(encId) });
    },
  });
}
